import { CheckCircle2, History, LoaderCircle, Send, Settings2, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { openSetupSection } from '../renderer/FirstReviewGuide';
import { hostExportStep, useHostExportFacts, type ExportNextStep } from '../renderer/host-export';
import { useSampleTourJob, useTourExportLine } from './TourExportScene';

/**
 * PAIGE: the tour's Export step on Paige (docs/engineering/workbench-port.md). The desktop's tour shows its first-export
 * card above the documents list, one line in CapturePoint's `export-ready` look, read from the desktop's own export
 * (`desktop-export`). Paige sends to its destinations, so this is that line from the host's export facts
 * (host-export.ts): the sample's exported count on the sample tour's job; Set up export, Sends as soon as ready, or
 * the deliveries that failed on the customer's own. One line, never a picture over the list (CapturePoint removed the
 * empty list's export scenes on 2026-10-31, 62ba97da, and the list no longer empties while a job has documents).
 */
export function HostExportPanel({
  jobId,
  documents,
}: {
  jobId: string;
  documents: readonly { needsReview: boolean; confirmedAsWhole?: boolean }[];
}) {
  const sampleTour = useSampleTourJob(jobId);
  const tourLine = useTourExportLine(jobId, sampleTour, documents.length);
  const facts = useHostExportFacts(jobId, !sampleTour);
  if (sampleTour) return tourLine;
  const ready = documents.filter(
    (document) => !document.needsReview || document.confirmedAsWhole === true,
  ).length;
  const step = facts && hostExportStep(facts, ready);
  return step ? <StepLine step={step} /> : null;
}

/** The line for the customer's own job: Set up export, the deliveries that failed, or Sends as soon as ready. */
function StepLine({ step }: { step: ExportNextStep }) {
  if (step.kind === 'not-set-up')
    return (
      <ExportLine
        icon={<Send size={16} />}
        words={`${(step.ready ?? 0).toLocaleString('en-US')} ready to send`}
      >
        <SetupButton stop="destination" primary>
          <Settings2 size={14} aria-hidden="true" />
          Set up export
        </SetupButton>
      </ExportLine>
    );
  if (step.kind === 'attention')
    return (
      <ExportLine
        icon={<TriangleAlert size={16} />}
        words={`${step.needsAttention.toLocaleString('en-US')} not delivered`}
      >
        <SetupButton stop="activity">
          <History size={14} aria-hidden="true" />
          See activity
        </SetupButton>
      </ExportLine>
    );
  if (step.kind !== 'automatic') return null;
  const today = step.sentToday.toLocaleString('en-US');
  return (
    <ExportLine
      icon={<CheckCircle2 size={16} />}
      tone="done"
      words={`Sends as soon as ready · ${today} today`}
    />
  );
}

/** A button that opens Job setup's Export at one of its stops. */
function SetupButton({
  stop,
  primary,
  children,
}: {
  stop: 'destination' | 'activity';
  primary?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={primary ? 'primary-button small' : 'secondary-button small'}
      onClick={() => openSetupSection('export', stop)}
    >
      {children}
    </button>
  );
}

/** One line in CapturePoint's first-export card look (onboarding.css, `export-ready`). */
export function ExportLine({
  icon,
  words,
  tone,
  children,
}: {
  icon: ReactNode;
  words: string;
  tone?: 'done' | 'exporting';
  children?: ReactNode;
}) {
  return (
    <section
      className={tone ? `export-ready ${tone}` : 'export-ready'}
      aria-label="Export"
      role="status"
    >
      <span className="export-ready-mark" aria-hidden="true">
        {icon}
      </span>
      <strong>{words}</strong>
      {children}
    </section>
  );
}

/** The spinning mark of a line whose export is still on its way. */
export const Sending = () => <LoaderCircle className="spin" size={16} />;
